/* eslint-disable react-hooks/exhaustive-deps */
import { func, string, object, bool } from 'prop-types'
import React, { useState, useEffect, useRef } from 'react';
import { TranslationContextProvider } from '../Shared/hooks/useTranslationContextHook'
import { UnauthorizedContextProvider } from '../Shared/hooks/useUnauthorizedContextHook'
import DoxFileSelector from "./components/doxFileSelector"
import DoxUploadList from "./components/doxUploadList"
import './dox-uploader.scss'
import '../../dox/dox.scss'
import '../../../../../components/library/buttons/Buttons.scss';
import { useContext } from 'react'
import doxExplorerCDMServices from '../Shared/services/doxExplorerCDMServices'
import DoxButton from '../Shared/components/DoxButton'
import useUploadQueue from './hooks/useUploadQueueHook'
import DoxUploadSuccessMessage from './components/doxUploadSuccessMessage'
import DoxUploadFailMessage from './components/doxUploadFailMessage'
import DoxModal from '../Shared/components/doxModal'
import { useInterviewInfo } from '../../hooks/useInterviewInfoHook'
import { useDoxExplorerParent } from '../../../../../hooks/useDoxExplorerParentHook';

export const DoxUploaderContext = React.createContext()

export const useDoxUploaderContext = () => useContext(DoxUploaderContext)

const DoxUploader = props => {

    const { ticket, layoutType, onUploadsComplete, show, onClose, isSignaturePageContext = false, filerName } = props
    const [, , , primaryName,] = useInterviewInfo(ticket);
    
    // Use filerName if provided (for Document Review context with Primary/Spouse), otherwise fall back to primaryName
    const clientName = filerName || primaryName;

    const [translateString, handleUnauthorized] = useDoxExplorerParent();

    // Track if component is mounted
    const isMountedRef = useRef(true);

    // file selector
    const [showingFileSelector, setShowingFileSelector] = useState(true)
    const [selectedFiles, setSelectedFiles] = useState([])

    // uploader
    const [existingFiles, setExistingFiles] = useState(null)
    const [cancelAll, setCancelAll] = useState(false)
    const [showingSuccessMessage, setShowingSuccessMessage] = useState(false)
    const [showingFailMessage, setShowingFailMessage] = useState(false)
   
    // handle the concurrent queue
    const { fileQueue, resetQueue, onUploadCompleted, onRetryUpload, areAllUploadsResolved, didAnyUploadsSucceed, fileUploadStatus } = useUploadQueue(selectedFiles)

    const loadExistingFiles = async () => {
        if (isMountedRef.current) {
            setExistingFiles(null)
        }
        let files = []
        try {
            var { status: st, fileList: fl } =
                await doxExplorerCDMServices.getFileList(handleUnauthorized, ticket.id)
            if (st) {
                files = fl
            }
        } catch (ex) {
            console.log(ex)
        } finally {
            if (isMountedRef.current) {
                setExistingFiles(files)
            }
        }
    }

    /**
     * handler that fires after files have been selected, either by D&D or file input.
     * @param {File[]} files
     */
    const handleFilesSelectedForUpload = files => {
        setSelectedFiles(files)
        setShowingFileSelector(false)
    }

    const handleNextClicked = e => {
        if (didAnyUploadsSucceed) {
            setShowingSuccessMessage(true)
        } else {
            setShowingFailMessage(true)
            handleDoneClicked(e)
        }
    }

    const handleDoneClicked = e => {
        // reset state values. Note: since they live higher up the component tree than the modal
        // they won't ever be unmounted when it closes.
        setShowingFileSelector(true)
        setSelectedFiles([])
        setExistingFiles(null)
        setCancelAll(false)
        setShowingSuccessMessage(false)
        setShowingFailMessage(false)
        resetQueue()

        // tell the parent component we're finished.
        if (onUploadsComplete) {
            onUploadsComplete(didAnyUploadsSucceed)
        }
    }  

    const handleModalClosed = (isManual) => {
        if (onClose) {
            onClose(isManual)
        }
        if (isManual) {
            // if the user clicks the "X", treat it the same as if they clicked 'done'
            handleDoneClicked()
        }
    }

    const handleUploadMoreClicked = e => {
        setExistingFiles(null)
        setSelectedFiles([])
        if (resetQueue) {
            resetQueue()
        }
        setShowingSuccessMessage(false)
        setShowingFailMessage(false)
        setShowingFileSelector(true)
        loadExistingFiles()
    }

    /* on load, fetch pre-existing tax year files (used for file duplicate detection) */
    useEffect(() => {
        if (show) {
            loadExistingFiles()
        } else {
            setExistingFiles(null)
        }
    }, [show])

    // Cleanup function to track component mount status
    useEffect(() => {
        isMountedRef.current = true;
        return () => isMountedRef.current = false;
    }, []);

    const value = {
        taxYear: ticket.taxYear, 
        ticketId: ticket.id,
        fileQueue,
        existingFiles,
        onUploadCompleted,
        onRetryUpload,
        primaryName: clientName,
        isSignaturePageContext
    }

    const hideX = !(showingFileSelector || showingSuccessMessage || (areAllUploadsResolved && !didAnyUploadsSucceed))

    return (
        <UnauthorizedContextProvider onHandleUnauthorizedUser={handleUnauthorized}>
            <TranslationContextProvider onTranslateString={translateString}>
                <DoxModal titleKey='Dox.DocumentUploader.Title' titleKeyParams={{ TaxYear: ticket.taxYear, ClientName: clientName }} show={show} uploadStatus={fileUploadStatus} onClose={handleModalClosed} hideCloseButton={hideX} showStatusIcon={true}>
                    <hr className='hr-nomargin' />
                    <DoxUploaderContext.Provider value={value}>
                        <div className='dox-content'>
                            {showingFileSelector &&
                                <DoxFileSelector layoutType={layoutType} onFilesSelected={handleFilesSelectedForUpload} />
                            }
                            {!showingFileSelector && !showingSuccessMessage &&
                                <DoxUploadList layoutType={layoutType} cancelAll={cancelAll} />
                            }
                            {showingSuccessMessage &&
                                <DoxUploadSuccessMessage />
                            }
                            {showingFailMessage &&
                                <DoxUploadFailMessage />
                            }
                        </div>
                    </DoxUploaderContext.Provider>
                    <div className='dox-upload-footer hrb-columns'>
                        <div className='hrb-column hrb-is-1 mr-3'>
                            {!showingSuccessMessage &&
                                <DoxButton
                                elementId='cancel-all-uploads'
                                buttonStyles='secondary'
                                disabled={(fileQueue || []).length === 0 || areAllUploadsResolved}
                                onClick={() => setCancelAll(true)}>
                                    {translateString('Dox.DocumentUploader.CTAs.CancelAllUploads')}
                                </DoxButton>
                            }
                            {showingSuccessMessage &&
                                <DoxButton
                                    elementId='upload-moar-things'
                                    buttonStyles='secondary'
                                    onClick={() => handleUploadMoreClicked()}>
                                    {translateString('Dox.DocumentUploader.CTAs.UploadMore')}
                                </DoxButton>
                            }
                            {showingFailMessage &&
                                <DoxButton
                                    elementId='upload-moar-things'
                                    buttonStyles='secondary'
                                    onClick={() => handleUploadMoreClicked()}>
                                    {translateString('Dox.DocumentUploader.CTAs.TryAgain')}
                                </DoxButton>
                            }
                        </div>
                        <div className='hrb-column hrb-is-1'>
                            {!showingSuccessMessage &&
                                <DoxButton
                                    elementId='next-confirm-uploads'
                                    buttonStyles='secondary'
                                    disabled={!areAllUploadsResolved}
                                    onClick={() => handleNextClicked()}>
                                    {translateString('Dox.DocumentUploader.CTAs.Next')}
                                </DoxButton>
                            }
                            {showingSuccessMessage &&
                                <DoxButton
                                    elementId='all-uploads-done'
                                    buttonStyles='secondary'
                                    onClick={() => handleDoneClicked()}>
                                    {translateString('Dox.DocumentUploader.CTAs.Done')}
                                </DoxButton>
                            }
                        </div>
                    </div>

                </DoxModal>
                
            </TranslationContextProvider>
        </UnauthorizedContextProvider>
    )
}

DoxUploader.propTypes = {
    layoutType: string.isRequired,
    ticket: object.isRequired,
    onUploadsComplete: func, // only called when the done button is clicked
    show: bool,
    onClose: func.isRequired, // called any time something closes the modal
    isSignaturePageContext: bool, // indicates if this is being used in Document Review context for signature page uploads
    filerName: string // optional: specific filer name (Primary or Spouse) for Document Review context
}

export default DoxUploader